import React, { useState } from 'react';
import { Navigate, useParams, useLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChartComponent from '../../components/ChartComponent';
import SvgChart from '../../components/SvgChart';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import { capitalizeFirstLetter, getBodyNameGenitive } from '../../utils/texts';
import './Home.css';
import AnalysesNavList from '../../components/analysesNav/AnalysesNavList';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import useDimensions from '../../hooks/useDimensions';
import { MeetingCard } from '../../components/meetings/MeetingCard';
import { PartiesDataResponseType, SessionsDataResponseType, VotesDataResponseType } from '../../types';
import Overview from '../../components/overview/Overview';

// Should be done on BE
const getMembers = (partiesData: PartiesDataResponseType[], leaderName: string) => {
    return partiesData
        .flatMap((party) =>
            party.clenove
                .filter((member) => member.aktivni === 1)
                .map((member) => ({
                    id: member.id,
                    firstName: member.jmeno.split(' ')[0],
                    lastName: member.jmeno.split(' ')[1],
                    partyName: party.strana_zkratka,
                    partyColor: party.strana_barva,
                    gender: member.pohlavi,
                    leader: member.jmeno === leaderName
                }))
        )
        .sort((a, b) => {
            const lastNameComparison = a.lastName.localeCompare(b.lastName, 'cs');
            if (lastNameComparison !== 0) return lastNameComparison;
            return a.firstName.localeCompare(b.firstName, 'cs');
        });
};

function Home() {
    const { bodyType } = useParams();
    const { t } = useTranslation();
    const { width } = useDimensions();
    const membersIncrement = width < 1200 ? 9 : 18;
    const [visibleMembersCount, setVisibleMembersCount] = useState(membersIncrement);

    // Should be replaced by generated api model types
    const response = useLoaderData() as [
        { data: any },
        { data: any },
        { data: PartiesDataResponseType[] },
        { data: VotesDataResponseType },
        { data: SessionsDataResponseType[] }
    ];
    const basicData = response[0].data;
    const municipality = response[1].data;
    const partiesData = response[2].data;
    const votesData = response[3].data;
    const meetingsData = response[4].data;
    const hasLatestMeeting = !!basicData.nejnovejsi_zasedani?.id;

    const members = getMembers(partiesData, basicData['leader']);
    const leader = members.find((member) => member.leader);

    if (basicData !== undefined && municipality !== undefined && partiesData !== undefined && votesData !== undefined) {
        const organ = bodyType ? municipality.organy[bodyType] : undefined;

        partiesData.forEach((party) => {
            let active_party = basicData.nejnovejsi_zasedani.mandaty.find(
                (obj: { strana_id: number }) => obj.strana_id === party.strana_id
            );
            if (active_party) {
                party['aktivni'] = true;
                party['pocet_mandatu'] = active_party.pocet_mandatu;
            } else {
                party['aktivni'] = false;
                party['pocet_mandatu'] = 0;
            }
        });

        const style = getComputedStyle(document.body);
        const colors = [style.getPropertyValue('--clr-1'), style.getPropertyValue('--clr-2')];

        /** Votes chart */
        const votesChartData = {
            labels: [''],
            datasets: [
                {
                    label: t('approved-proposals'),
                    data: [votesData.pocet_prijatych],
                    backgroundColor: [colors[0]],
                    barPercentage: 0.8
                },
                {
                    label: t('rejected-proposals'),
                    data: [votesData.pocet_neprijatych],
                    backgroundColor: [colors[1]],
                    barPercentage: 0.8
                }
            ]
        };

        var hover_timeout: NodeJS.Timeout;
        const votesChartOptions = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            aspectRatio: 1.4,
            scales: {
                x: {
                    stacked: true
                },
                y: {
                    stacked: true
                }
            },
            plugins: {
                tooltips: {
                    mode: 'index',
                    intersect: false
                },
                legend: {
                    labels: {
                        generateLabels: (chart: { data: { datasets: any } }) => {
                            const datasets = chart.data.datasets;
                            return datasets.map((data: { label: any; data: any[]; backgroundColor: any }, i: any) => ({
                                text: `${data.label} (${data.data[0]})`,
                                fillStyle: data.backgroundColor,
                                strokeStyle: 'white',
                                datasetIndex: 0,
                                index: i
                            }));
                        }
                    },
                    onHover: function (
                        evt: { native: { target: { style: { cursor: string } } } },
                        item: { index: any },
                        legend: { chart: { tooltip: any; data: { datasets: any[] }; update: () => void } }
                    ) {
                        const tooltip = legend.chart.tooltip;
                        if (tooltip.getActiveElements().length > 0) {
                            tooltip.setActiveElements([], { x: 0, y: 0 });
                        }
                        tooltip.setActiveElements([{ datasetIndex: item.index, index: 0 }], { x: 0, y: 0 });

                        evt.native.target.style.cursor = 'pointer';
                        legend.chart.data.datasets.forEach((i: { backgroundColor: string }, id: any) => {
                            if (id !== item.index) {
                                i.backgroundColor += '30';
                            }
                        });
                        clearTimeout(hover_timeout);
                        hover_timeout = setTimeout(function () {
                            legend.chart.update();
                        }, 60);
                    },
                    onLeave: function (
                        evt: any,
                        item: { index: any },
                        legend: { chart: { tooltip: any; data: { datasets: any[] }; update: () => void } }
                    ) {
                        const tooltip = legend.chart.tooltip;
                        tooltip.setActiveElements([], { x: 0, y: 0 });
                        legend.chart.data.datasets.forEach((i: { backgroundColor: string | any[] }, id: any) => {
                            if (id !== item.index) {
                                i.backgroundColor = i.backgroundColor.slice(0, -2);
                            }
                        });
                        legend.chart.update();
                    }
                }
            }
        };

        return (
            <>
                <Intro title={t(organ.nazev)} subtitle={basicData['funkcni_obdobi']} />
                <SvgChart basicData={basicData} />
                <Overview basicData={basicData} partiesData={partiesData} />
                {hasLatestMeeting && (
                    <Section
                        title={capitalizeFirstLetter(t('meetings'))}
                        subtitle={`${t('in-term', {
                            ns: 'meetings',
                            num: meetingsData.length,
                            where: bodyType ? municipality.organy[bodyType].misto : '',
                            type: getBodyNameGenitive(bodyType)
                        })} ${t('more-meetings')}`}
                        contentClassName='card-list'
                        showMoreChevronLinkProps={{
                            text: t('show_meetings'),
                            link: 'zasedani'
                        }}
                    >
                        {meetingsData.slice(0, 3).map((meeting, index) => {
                            return <MeetingCard key={index} meetingData={meeting} />;
                        })}
                    </Section>
                )}

                <Section
                    title={t('Composition', { ns: 'translation', context: bodyType })}
                    showMoreChevronLinkProps={{
                        link: 'zastupitele',
                        text: t('show_members')
                    }}
                    showMoreContentProps={{
                        condition: visibleMembersCount < members.length,
                        onClick: () => setVisibleMembersCount(visibleMembersCount + membersIncrement)
                    }}
                >
                    {leader && (
                        <>
                            <h4>{capitalizeFirstLetter(basicData['leader_funkce'])}</h4>
                            <MemberCard
                                memberData={leader}
                                partyData={{ partyName: leader.partyName, partyColor: leader.partyColor }}
                            />
                        </>
                    )}
                    <h4>{t('Members')}</h4>
                    <div className='member-card-list'>
                        {members.slice(0, visibleMembersCount).map((member, index) => {
                            if (!member.leader) {
                                return (
                                    <MemberCard
                                        key={index}
                                        memberData={member}
                                        partyData={{ partyName: member.partyName, partyColor: member.partyColor }}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                </Section>
                {hasLatestMeeting && (
                    <>
                        <Section
                            title={t('Votings')}
                            subtitle={t('votings-tooltip')}
                            contentClassName='votings-section-content'
                            showMoreChevronLinkProps={{
                                text: t('show_votings'),
                                link: 'hlasovani'
                            }}
                        >
                            <div className='votings-summary-wrapper'>
                                <div className='votings-summary-item'>
                                    <h3>
                                        <span className='material-symbols-rounded'>stacks</span>
                                        {basicData.pocet_hlasovani.toLocaleString('cz')}
                                    </h3>
                                    <p>{t('valid_votes')}</p>
                                </div>
                                <div className='votings-summary-item'>
                                    <h3>
                                        <span className='material-symbols-rounded'>select_check_box</span>
                                        {basicData['pocet_hlasovani_clenu'].toLocaleString('cz')}
                                    </h3>
                                    <p>{t('all-members-votes')}</p>
                                </div>
                            </div>
                            <ChartComponent
                                type='bar'
                                data={votesChartData}
                                options={votesChartOptions}
                                height='200px'
                                width={undefined}
                                xTitle={undefined}
                                yTitle={undefined}
                                aria={undefined}
                            />
                        </Section>
                        <Section
                            title={t('analyses')}
                            subtitle={t('title-tooltip', {
                                ns: 'analyses'
                            })}
                            showMoreChevronLinkProps={{
                                text: t('show_analyses'),
                                link: 'analyzy'
                            }}
                        >
                            <AnalysesNavList />
                        </Section>
                    </>
                )}
            </>
        );
    } else {
        return <Navigate to='/' />;
    }
}

export default Home;
